import { PropsWithChildren, useState, useRef, useEffect } from "react";
import { Outlet, Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Gravatar from 'react-gravatar';
import { Avatar, Alert, Box, Grid, IconButton, List, ListItem, ListItemButton, Link as MuiLink, Menu, Typography } from "@mui/material";
import { logOut as makeLogoutRequest } from "../clients/FixieApiClient";
import { NavigationButtonLink } from '../components/Link';
import logo from "../images/logo.svg";
import { Account, Profile, VercelIntendedPlatformName } from "../models";

// ----------------------------------------------------------------------

const StyledRoot = styled("div")({
  display: "block",
  minHeight: "100vh",
  overflow: "hidden",
  paddingBottom: "32px"
});

const Background = styled("div")({
  backgroundColor: "rgb(246,248,250)",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: -1
});

const Main = styled("div")(({ theme }) => ({
  flexGrow: 1,
  overflow: "auto",
  backgroundColor: "#ffffff",
  boxShadow: "0 0 2px 0 rgba(0,0,0,0.1)",
  padding: 0,
  minHeight: "calc(100vh - 128px)",
  marginRight: theme.spacing(4),
  borderRadius: theme.spacing(1)
}));

// ----------------------------------------------------------------------

interface DashboardProps extends PropsWithChildren {
  profile: Profile
  account: Account
}

export default function Dashboard(props: DashboardProps) {
  const logOut = async () => {
    await makeLogoutRequest();
    window.location.href = "/login"; // Do a full page reload to clear all state
  };

  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isIntendedPlatformCTAVisible, setIntendedPlatformCTAVisible] = useState(false);

  const toggleAvatarMenu = () => {
    setIsAvatarMenuOpen(!isAvatarMenuOpen);
  };
  const closeAvatarMenu = () => { setIsAvatarMenuOpen(false) };
  const avatarRef = useRef();

  useEffect(() => {
    const isVisible = localStorage.getItem('intendedPlatformCTAVisible');
    if (isVisible === 'false') {
      setIntendedPlatformCTAVisible(false);
    } else {
      setIntendedPlatformCTAVisible(true);
    }
  }, []);

  const hideBanner = () => {
    localStorage.setItem('intendedPlatformCTAVisible', 'false');
    setIntendedPlatformCTAVisible(false);
  };

  const getIntendedPlatformCallToAction = () => {
    if (isIntendedPlatformCTAVisible && props.account.intendedPlatform && props.account.intendedPlatform === VercelIntendedPlatformName && props.account.linkedToVercel !== true) {
      const vercelIntegrationUrl = 'https://vercel.com/integrations/fixie';
      return <Alert severity="info" onClose={hideBanner}>
        Finish connecting your Fixie account to Vercel.
        Go to <MuiLink style={{ textDecoration: "none" }} href={vercelIntegrationUrl}>Fixie Integration</MuiLink> and press <strong>Add Integration</strong> to complete.
      </Alert>
    }
  }

  return (
    <StyledRoot>
      <Background />
      <header>
        <Box margin="16px 32px" display="flex" justifyContent="space-between" alignItems='center'>
          <Box flexGrow={1}>
            <Link to="/apps">
              <img src={logo} alt="Fixie" height="32px" />
            </Link>
          </Box>
          <Box flexGrow={0} sx={{ mr: 2 }}>
            <Typography variant="body1" color="gray">{props.account.accountName}</Typography>
          </Box>
          <Box ref={avatarRef} flexGrow={0}>
            <IconButton onClick={toggleAvatarMenu}>
              <Avatar>{props.profile && <Gravatar email={props.profile.email} />}</Avatar>
            </IconButton>
            <Menu open={isAvatarMenuOpen} anchorEl={avatarRef.current} onClose={closeAvatarMenu} >
              <NavigationButtonLink to="/profile" onClick={closeAvatarMenu}>Profile</NavigationButtonLink>
              <ListItemButton onClick={logOut}>Log Out</ListItemButton>
            </Menu>
          </Box>
        </Box>
      </header>
      <Grid container display="flex" flexWrap="nowrap">
        <Box minHeight="128px" width="128px" flexGrow={0} flexShrink={0}>
          <nav className="leftNav">
            <List>
              <ListItem><NavigationButtonLink to="/apps">Apps</NavigationButtonLink></ListItem>
              <ListItem><NavigationButtonLink to="/users">Team</NavigationButtonLink></ListItem>
              <ListItem><ListItemButton href={`/api/billing-console`}>Billing</ListItemButton></ListItem>
            </List>
          </nav>
        </Box>
        <Main>
          {getIntendedPlatformCallToAction()}
          <Outlet />
        </Main>
      </Grid>
    </StyledRoot >
  );
}
